import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, Heart, Truck, Sun, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_2ad418ae-721e-40fd-824f-e8019fae87b8/artifacts/6ngr2f4g_IMG-20230607-WA0001.jpg";
const PRODUCTS_IMAGE = "https://images.unsplash.com/photo-1754891144113-8a8d092a7dbf?crop=entropy&cs=srgb&fm=jpg&q=85";
const HANDS_IMAGE = "https://images.unsplash.com/photo-1597868165956-03a6827955b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxvcmdhbmljJTIwZmFybSUyMHZlZ2V0YWJsZXMlMjBoYXJ2ZXN0JTIwaGFuZHMlMjBzb2lsfGVufDB8fHx8MTc3MTYyNDE2N3ww&ixlib=rb-4.1.0&q=85";
const FAMILY_IMAGE = "https://images.unsplash.com/photo-1657288649124-b80bdee3c17e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHx2ZWdldGFibGUlMjBiYXNrZXQlMjBkZWxpdmVyeSUyMGZhcm0lMjBmcmVzaHxlbnwwfHx8fDE3NzE2MjMxOTF8MA&ixlib=rb-4.1.0&q=85";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const values = [
  {
    icon: Leaf,
    title: "Sustentabilidade",
    description: "Práticas agrícolas que respeitam a terra e o ambiente, garantindo um futuro melhor."
  },
  {
    icon: Heart,
    title: "Qualidade",
    description: "Produtos frescos colhidos com cuidado, do nosso campo diretamente para a sua mesa."
  },
  {
    icon: Truck,
    title: "Proximidade",
    description: "Entregas locais na região de Braga e Vila Verde, com frescura garantida."
  },
  {
    icon: Sun,
    title: "Tradição",
    description: "Um projeto familiar com raízes na terra e no amor pela agricultura portuguesa."
  }
];

const basketTypes = [
  {
    name: "Cabaz Individual",
    description: "6 kg de produtos frescos por apenas 16€. Ideal para 1-2 pessoas.",
    features: ["6 kg de produtos", "16€", "Entrega semanal"]
  },
  {
    name: "Cabaz Familiar",
    description: "9 kg de produtos frescos por 20€. Perfeito para famílias de 3-4 pessoas.",
    features: ["9 kg de produtos", "20€", "Entrega semanal"]
  },
  {
    name: "Encomenda à Lista",
    description: "Escolha os produtos que pretende da lista semanal. Mínimo de encomenda: 16€.",
    features: ["Lista personalizada", "mín. 16€", "Flexibilidade total"]
  }
];

export default function HomePage() {
  return (
    <div data-testid="home-page" className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center pt-20">
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_IMAGE}
            alt="Cabaz de legumes frescos"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#F3F1EB] via-[#F3F1EB]/90 to-[#F3F1EB]/30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#F3F1EB]/40 via-transparent to-[#F3F1EB]/20" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="max-w-2xl"
          >
            <motion.span
              variants={fadeInUp}
              className="inline-block font-accent text-2xl text-[#1B93A4] mb-4"
            >
              Bem-vindo à Quinta
            </motion.span>

            <motion.h1
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#2C2C2C] leading-tight mb-6"
              style={{ textShadow: "0 2px 16px rgba(243, 241, 235, 0.85)" }}
            >
              Do campo para a
              <span className="text-[#3A4A38]"> sua mesa</span>
            </motion.h1>

            <motion.p
              variants={fadeInUp}
              className="text-lg text-[#2C2C2C]/80 mb-8 leading-relaxed"
            >
              Descubra o sabor autêntico dos produtos locais. Cabazes semanais de
              legumes e frutas frescas, cultivados de forma sustentável e entregues
              diretamente em sua casa.
            </motion.p>

            <motion.div variants={fadeInUp} className="flex flex-wrap gap-4">
              <Link to="/produtos" data-testid="hero-cta-products">
                <Button className="btn-primary h-14 px-8 text-base">
                  Ver Cabazes
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contacto" data-testid="hero-cta-contact">
                <Button variant="outline" className="btn-secondary h-14 px-8 text-base">
                  Falar Connosco
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="image-hover rounded-xl overflow-hidden"
            >
              <img
                src={HANDS_IMAGE}
                alt="Mãos a colher produtos frescos"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-accent text-2xl text-[#1B93A4]">
                A Nossa História
              </span>
              <h2 className="text-3xl lg:text-4xl font-semibold text-[#2C2C2C] mt-2 mb-6">
                Quinta da Oliveirinha
              </h2>
              <div className="gold-line mb-6" />
              <p className="text-[#2C2C2C]/80 mb-4 leading-relaxed">
                Somos uma família apaixonada pela terra e pela agricultura tradicional
                portuguesa. Na Quinta da Oliveirinha, em Vila Verde, cultivamos legumes
                e frutas com métodos sustentáveis, respeitando os ciclos da natureza.
              </p>
              <p className="text-[#2C2C2C]/80 mb-6 leading-relaxed">
                O nosso objetivo é levar até ti o melhor que a terra tem para oferecer
                – produtos frescos, saborosos e cultivados com carinho, diretamente da
                nossa horta para a sua mesa.
              </p>
              <Link to="/sobre" data-testid="intro-cta-about">
                <Button variant="link" className="text-[#1B93A4] p-0 h-auto text-base font-medium">
                  Conhecer a nossa história
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 lg:py-28 bg-[#F3F1EB] noise-overlay">
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="font-accent text-2xl text-[#1B93A4]">
              Os Nossos Valores
            </span>
            <h2 className="text-3xl lg:text-4xl font-semibold text-[#2C2C2C] mt-2">
              O que nos define
            </h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {values.map((value, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="bg-white border-[#D4C4A8]/50 h-full card-hover" data-testid={`value-card-${index}`}>
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 bg-[#3A4A38]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <value.icon className="w-7 h-7 text-[#3A4A38]" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#2C2C2C] mb-2">
                      {value.title}
                    </h3>
                    <p className="text-sm text-[#2C2C2C]/70">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Baskets Preview Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <span className="font-accent text-2xl text-[#1B93A4]">
                Frescos da Horta
              </span>
              <h2 className="text-3xl lg:text-4xl font-semibold text-[#2C2C2C] mt-2 mb-6">
                Os Nossos Cabazes
              </h2>
              <div className="gold-line mb-6" />
              <p className="text-[#2C2C2C]/80 mb-8 leading-relaxed">
                Todas as semanas preparamos cabazes com os melhores produtos da
                nossa horta. Legumes e frutas da época, colhidos no ponto certo
                de maturação para garantir o máximo sabor e frescura.
              </p>

              <div className="space-y-4 mb-8">
                {basketTypes.map((basket, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3"
                    data-testid={`basket-preview-${index}`}
                  >
                    <CheckCircle2 className="w-5 h-5 text-[#1B93A4] mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-medium text-[#2C2C2C]">
                        {basket.name}
                      </span>
                      <span className="text-[#2C2C2C]/70"> – {basket.description}</span>
                    </div>
                  </div>
                ))}
              </div>

              <Link to="/produtos" data-testid="baskets-cta">
                <Button className="btn-primary">
                  Ver todos os cabazes
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="order-1 lg:order-2 image-hover rounded-xl overflow-hidden"
            >
              <img
                src={PRODUCTS_IMAGE}
                alt="Variedade de legumes frescos"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works Preview */}
      <section className="py-20 lg:py-28 bg-[#3A4A38] text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="font-accent text-2xl text-[#D4C4A8]">
              Simples e Prático
            </span>
            <h2 className="text-3xl lg:text-4xl font-semibold mt-2">
              Como Funciona
            </h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {[
              { step: "01", title: "Produção", desc: "Cultivamos os produtos na nossa horta com métodos sustentáveis." },
              { step: "02", title: "Colheita", desc: "Colhemos frescos, no dia anterior à entrega." },
              { step: "03", title: "Preparação", desc: "Preparamos o seu cabaz com carinho e cuidado." },
              { step: "04", title: "Entrega", desc: "Entregamos em sua casa, na região de Braga e Vila Verde." }
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="text-center"
                data-testid={`step-${index}`}
              >
                <span className="text-5xl font-semibold text-[#D4C4A8]/30">
                  {item.step}
                </span>
                <h3 className="text-xl font-semibold mt-2 mb-3">{item.title}</h3>
                <p className="text-white/70 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="text-center mt-12"
          >
            <Link to="/como-funciona" data-testid="how-cta">
              <Button className="bg-white text-[#3A4A38] hover:bg-white/90 rounded-full px-8 py-6 text-base font-medium">
                Saber mais
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-custom">
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src={FAMILY_IMAGE}
              alt="Família a desfrutar de refeição"
              className="w-full h-[400px] lg:h-[450px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#3A4A38]/90 to-[#3A4A38]/60" />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="absolute inset-0 flex items-center"
            >
              <div className="container-custom">
                <div className="max-w-xl text-white">
                  <span className="font-accent text-2xl text-[#D4C4A8]">
                    Pronto para começar?
                  </span>
                  <h2 className="text-3xl lg:text-4xl font-semibold mt-2 mb-6">
                    Encomende o seu primeiro cabaz
                  </h2>
                  <p className="text-white/80 mb-8">
                    Fale connosco para saber mais sobre os nossos cabazes e
                    confirmar a disponibilidade de entrega na sua zona.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <Link to="/contacto" data-testid="final-cta-contact">
                      <Button className="bg-white text-[#3A4A38] hover:bg-white/90 rounded-full px-8 py-6 text-base font-medium">
                        Encomendar Agora
                      </Button>
                    </Link>
                    <a
                      href="tel:+351938173638"
                      data-testid="final-cta-phone"
                      className="inline-flex items-center gap-2 text-white hover:text-[#D4C4A8] transition-colors"
                    >
                      <span className="font-medium">+351 938 173 638</span>
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
